import React, { useMemo } from "react";

// Third-party
import PropTypes from "prop-types";

// Material UI
import { useTheme } from "@material-ui/core";

// Components
import OpenLayersFooter from "features/Maps/OpenLayersFooter";

// Contexts
import { useUiContext } from "context/UiContext";

/**
 * Renders a map container with dynamic padding based on UI state.
 * Includes a footer component and applies theme-based styling.
 *
 * @param {Object} props - Component props
 * @param {React.RefObject} props.refEle - Reference to the map DOM element
 * @returns {React.ReactElement} The rendered MapObject component
 */
const OpenLayersMap = ({ refEle }) => {
  const [uiState] = useUiContext();
  const theme = useTheme();

  /**
   * Computes padding values for the map container based on drawer states.
   * @returns {Object} Object containing padding values (left, right, bottom, top)
   */
  const { paddingLeft, paddingRight, paddingBottom, paddingTop } =
    useMemo(() => {
      // Padding left
      let paddingLeft = 0;
      if (uiState.leftDrawerWidth === 0) {
        paddingLeft = 10;
      } else {
        paddingLeft = uiState.leftDrawerWidth;
      }
      // Padding right
      let paddingRight = 0;
      if (uiState.rightDrawerWidth === 0) {
        paddingRight = 10;
      } else {
        paddingRight = uiState.rightDrawerWidth + 20;
      }
      // Padding bottom
      let paddingBottom = 0;
      if (uiState.bottomDrawerOpen) {
        paddingBottom = uiState.bottomDrawerHeight;
      }
      // Padding top
      let paddingTop = 48;
      return { paddingLeft, paddingRight, paddingBottom, paddingTop };
    }, [uiState]);

  return (
    <>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          paddingTop,
          paddingLeft,
          paddingRight,
          paddingBottom,
          transition: "all 0.2s linear",
          backgroundColor: theme.palette.primary.main
        }}
      >
        <div
          id="map"
          ref={refEle}
          style={{
            width: "100%",
            height: "100%",
            transition: "all 0.2s linear",
            backgroundColor: "white"
          }}
        />
      </div>
      <OpenLayersFooter />
    </>
  );
};

OpenLayersMap.propTypes = {
  refEle: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })
};

export default OpenLayersMap;
